import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, } from 'react';
import { useCurrentAccount } from '@mysten/dapp-kit-react';
import { usePositionCap } from '../hooks/usePositionCap';
import { useAlphalendEventSubscription } from '../hooks/useAlphalendEventSubscription';
const PositionContext = createContext(undefined);
// Helper function to calculate position summary from raw data
const calculatePositionSummary = (id, positionData) => {
    if (!positionData) {
        return {
            id,
            netWorth: 0,
            totalSupplied: 0,
            totalBorrowed: 0,
            borrowLimitUsedPercentage: 0,
            safeLimitPercentage: 0,
            liquidationPercentage: 0,
        };
    }
    const totalSupplied = parseFloat(positionData.total_supplied_usd || '0');
    const totalBorrowed = parseFloat(positionData.total_borrowed_usd || '0');
    const netWorth = parseFloat(positionData.net_worth || '0');
    const borrowLimit = parseFloat(positionData.safe_borrow_limit || '0');
    const liquidationLimit = parseFloat(positionData.liquidation_limit || '0');
    const borrowLimitUsed = parseFloat(positionData.borrow_limit_used || '0');
    const borrowLimitUsedPercentage = totalSupplied > 0 ? (borrowLimitUsed / totalSupplied) * 100 : 0;
    const safeLimitPercentage = totalSupplied > 0 ? (borrowLimit / totalSupplied) * 100 : 0;
    const liquidationPercentage = totalSupplied > 0 ? (liquidationLimit / totalSupplied) * 100 : 0;
    return {
        id,
        netWorth,
        totalSupplied,
        totalBorrowed,
        borrowLimitUsedPercentage,
        safeLimitPercentage,
        liquidationPercentage,
    };
};
/**
 * Individual position subscriber component
 * Each instance subscribes to one position's data
 */
const PositionSubscriber = ({ positionId, onDataUpdate }) => {
    const { data } = useAlphalendEventSubscription(`portfolio:${positionId}`, `portfolio:${positionId}_high_freq`);
    useEffect(() => {
        onDataUpdate(positionId, data || null);
    }, [data, positionId, onDataUpdate]);
    return null;
};
export const PositionProvider = ({ children }) => {
    const currentAccount = useCurrentAccount();
    const { positionCapIds, loading, error } = usePositionCap('multiple');
    const [selectedPositionCapId, setSelectedPositionCapId] = useState(null);
    // Newly created position IDs added immediately so they show up before the chain confirms
    const [extraPositionIds, setExtraPositionIds] = useState([]);
    const [positionDataMap, setPositionDataMap] = useState(new Map());
    // Merge chain-fetched IDs with any newly created ones not yet returned by the chain
    const allKnownPositionIds = [
        ...positionCapIds,
        ...extraPositionIds.filter((id) => !positionCapIds.includes(id)),
    ];
    const allKnownKey = allKnownPositionIds.join(',');
    // Drop extra IDs once they appear in the real chain-fetched list
    useEffect(() => {
        if (extraPositionIds.length === 0)
            return;
        const stillExtra = extraPositionIds.filter((id) => !positionCapIds.includes(id));
        if (stillExtra.length !== extraPositionIds.length) {
            setExtraPositionIds(stillExtra);
        }
    }, [positionCapIds, extraPositionIds]);
    // Auto-select first position when positions are loaded
    useEffect(() => {
        if (allKnownPositionIds.length > 0 && !selectedPositionCapId) {
            const address = currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address;
            if (address) {
                const lastSelectedKey = `last-selected-position-${address}`;
                const lastSelected = localStorage.getItem(lastSelectedKey);
                if (lastSelected && allKnownPositionIds.includes(lastSelected)) {
                    setSelectedPositionCapId(lastSelected);
                }
                else {
                    setSelectedPositionCapId(allKnownPositionIds[0]);
                }
            }
            else {
                setSelectedPositionCapId(allKnownPositionIds[0]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allKnownKey, selectedPositionCapId, currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address]);
    // Deselect current position if it no longer exists in the list
    useEffect(() => {
        if (selectedPositionCapId && !allKnownPositionIds.includes(selectedPositionCapId)) {
            setSelectedPositionCapId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPositionCapId, allKnownKey]);
    // Clear selected position when wallet disconnects
    useEffect(() => {
        if (!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address)) {
            setSelectedPositionCapId(null);
        }
    }, [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address]);
    const addPositionId = useCallback((positionCapId) => {
        setExtraPositionIds((prev) => (prev.includes(positionCapId) ? prev : [...prev, positionCapId]));
    }, []);
    const selectPosition = (positionCapId) => {
        setSelectedPositionCapId(positionCapId);
        const address = currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address;
        if (address) {
            const lastSelectedKey = `last-selected-position-${address}`;
            localStorage.setItem(lastSelectedKey, positionCapId);
        }
    };
    // Handle position data updates
    const handleDataUpdate = useCallback((id, data) => {
        setPositionDataMap((prev) => {
            const newMap = new Map(prev);
            newMap.set(id, data);
            return newMap;
        });
    }, []);
    // Get position summary
    const getPositionSummary = useCallback((positionId) => {
        const data = positionDataMap.get(positionId);
        return calculatePositionSummary(positionId, data);
    }, [positionDataMap]);
    // Get all positions summary
    const getAllPositionsSummary = useCallback((ids) => {
        return ids.map((id) => {
            const data = positionDataMap.get(id);
            return calculatePositionSummary(id, data);
        });
    }, [positionDataMap]);
    // Clear old position data and fetch latest
    const clearPositionData = useCallback((positionId) => {
        setPositionDataMap((prev) => {
            const newMap = new Map(prev);
            newMap.delete(positionId);
            return newMap;
        });
    }, []);
    return (_jsxs(PositionContext.Provider, { value: {
            selectedPositionCapId,
            allPositionCapIds: allKnownPositionIds,
            selectPosition,
            addPositionId,
            loading,
            error,
            getPositionSummary,
            getAllPositionsSummary,
            clearPositionData,
        }, children: [allKnownPositionIds.map((positionId) => (_jsx(PositionSubscriber, { positionId: positionId, onDataUpdate: handleDataUpdate }, positionId))), children] }));
};
export const usePosition = () => {
    const context = useContext(PositionContext);
    if (context === undefined) {
        throw new Error('usePosition must be used within a PositionProvider');
    }
    return context;
};
